import { StyleSheet } from 'react-native'

import { FigmaAlertCircleOutlineIcon } from '../icons/FigmaSystemIcons'
import { Box, Text, useTheme } from '../theme/restyle'

/** 2812:1141 renders at 26 × 26. */
const ICON_SIZE = 26

export interface FiatFailureBannerProps {
  title: string
  /** The provider's own reason, when it gave one. */
  detail: string
}

/**
 * The payment-failure banner on the amount screen, Fun Mobile V2 2812:1135.
 *
 * It covers the header rather than taking the inline notice slot, so the
 * amount and the action below it hold their places while the buyer reads why
 * the payment did not go through.
 */
export function FiatFailureBanner({ title, detail }: FiatFailureBannerProps) {
  const theme = useTheme()
  const accent = theme.content.elements.primary
  return (
    <Box
      flexDirection="row"
      accessibilityRole="alert"
      style={[styles.card, { backgroundColor: theme.background.modal }]}
    >
      {/* Opaque, because the header is underneath: the design's translucent
          fill lands on 10% of the content colour, which reads the same over
          the dark modal and inverts on a light host. It rounds its own corners
          rather than being clipped — iOS drops the card's shadow the moment
          the card clips. */}
      <Box
        pointerEvents="none"
        style={[
          StyleSheet.absoluteFill,
          styles.wash,
          { backgroundColor: theme.content.elements.ghosted },
        ]}
      />
      <Box style={styles.glyph}>
        <FigmaAlertCircleOutlineIcon size={ICON_SIZE} color={accent} />
      </Box>
      <Box flex={1} style={styles.copy}>
        <Text weight="medium" style={[styles.text, { color: accent }]}>
          {title}
        </Text>
        <Text style={[styles.text, styles.detail, { color: accent }]}>
          {detail}
        </Text>
      </Box>
    </Box>
  )
}

const styles = StyleSheet.create({
  // 364x92 at radius 20, with the glyph centred 34 in from the card's left
  // and the copy starting at 64.
  card: {
    minHeight: 92,
    borderRadius: 20,
    paddingVertical: 18,
    paddingLeft: 21,
    paddingRight: 10,
    gap: 17,
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 4 },
    elevation: 4,
  },
  wash: { borderRadius: 20 },
  glyph: { justifyContent: 'center' },
  copy: { justifyContent: 'center' },
  text: { fontSize: 15, lineHeight: 18, letterSpacing: -0.225 },
  // Figma caps the reason at 256 so it breaks a line earlier than the card's
  // own width would.
  detail: { opacity: 0.5, maxWidth: 256 },
})
